import { action, makeObservable, observable } from 'mobx';

import { IApiClient } from '../data/client';
import { GetConfigurationCodeByEan13CodeQuery } from '../data/model';
import { ConfiguratorUrlBuilder } from '../errorHandling/ParseConfigurationCodeAndRedirectState';
import { ConfigurationFinderTranslation } from '../localization/SiteTranslation';
import { AsyncCommand } from './common';
import { ConfigurationCodeRule, TextInput } from './Form';
import { isEan13Barcode } from './isEan13Barcode';
import { isModularConfigurationCode } from './isModularConfigurationCode';
import { ModularConfiguratorUrlBuilder } from './ModularConfiguratorUrlBuilder';
import { INavigationService } from './NavigationService';
import { StatusCodes } from './StatusCodes';

class ConfigurationFinderState {
  @observable
  public errorMessage: string;

  public searchBar: TextInput;

  public searchCommand: AsyncCommand;

  public constructor(
    private readonly client: IApiClient,
    private readonly navigationService: INavigationService,
    private readonly translation: ConfigurationFinderTranslation,
  ) {
    makeObservable(this);
    this.searchBar = new TextInput().withRule(new ConfigurationCodeRule());
    this.searchCommand = new AsyncCommand(this.goToConfigurator, this.searchBar);
  }

  @action.bound
  private async goToConfigurator() {
    const codeSent = this.searchBar.value;
    this.setErrorMessage('');

    try {
      const url = await this.resolveConfiguratorUrl(codeSent);
      this.navigationService.navigateTo(url);
    } catch (error) {
      if (error.status) {
        if (error.status === StatusCodes.NotFound) {
          this.setErrorMessage(this.translation.codeNotFound.interpolate([['notFoundCode', codeSent]]));
          return;
        } else if (error.status === StatusCodes.BadRequest) {
          this.setErrorMessage(this.translation.codeInvalid.interpolate([['invalidCode', codeSent]]));
          return;
        }
      }

      throw error;
    }
  }

  private async resolveConfiguratorUrl(code: string) {
    if (isEan13Barcode(code)) {
      const result = await this.client.send(new GetConfigurationCodeByEan13CodeQuery({ ean13Code: code }));
      code = result.code;
    }

    const urlBuilder = isModularConfigurationCode(code)
      ? new ModularConfiguratorUrlBuilder(this.client)
      : new ConfiguratorUrlBuilder(this.client);

    return await urlBuilder.buildFromCode(code);
  }

  @action
  private setErrorMessage(message: string) {
    this.errorMessage = message;
  }
}

export default ConfigurationFinderState;
